import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {SquareButton} from "@/scenes.ui/square-button";
import {Fonts} from "@/constants/font";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import {Tr} from "@/i18n/i18n";
import PlayerStore from "@/store/player";
import Sprite = Phaser.GameObjects.Sprite;
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import {CardInfo, CardType, EvolutionInfo} from "@/store/card/types";
import {SoloIntroPanel} from "@/scenes.ui/solo-intro-panel";
import CardStore from "@/store/card";
import {EvolutionLink} from "@/scenes.ui/EvolutionLink";

export class CardPanel extends VerticalStandardPanel {
    private completedSound?: Phaser.Sound.BaseSound;
    private clickSound?: Phaser.Sound.BaseSound;
    private cards: CardInfo[];
    private currentIndex: number = 0;
    private dynamicElements: Record<string, Phaser.GameObjects.GameObject | undefined> = {};

    constructor(
        scene: Scene,
        canvas: HTMLCanvasElement,
        cards: CardInfo[],
        useCardCallback: (cardType: CardType) => unknown,
        clickSound?: Phaser.Sound.BaseSound,
        completedSound?: Phaser.Sound.BaseSound,
    ) {
        super(scene, canvas, Tr('cards'));
        this.setDepth(3002);
        this.completedSound = completedSound;
        this.clickSound = clickSound;
        this.cards = cards;

        const activeCardType = PlayerStore.GET_CARDS_STATE()?.activeCard;
        this.cards = cards.filter(card => card.count > 0 || (activeCardType && card.cardType === activeCardType)
        );
        if (this.cards.length === 0 && !activeCardType) {
            this.close();
            return;
        }
        this.refreshPanel(scene, canvas, useCardCallback);
    }

    private clear(): void {
        Object.entries(this.dynamicElements).forEach(([key, element]) => {
            if (element) {
                element.destroy();
            }
        });
        this.dynamicElements = {
            leftArrow: this.dynamicElements.leftArrow,
            rightArrow: this.dynamicElements.rightArrow,
        };
    }


    private async updateCardInfo(cardType: CardType): Promise<CardInfo> {
        try {
            const response = await CardStore.fetchCardsInfo();
            const updatedCard = response.find(card => card.cardType === cardType);
            if (updatedCard) {
                this.cards = response.filter(card =>
                    card.count > 0 ||
                    (PlayerStore.GET_CARDS_STATE()?.activeCard &&
                        card.cardType === PlayerStore.GET_CARDS_STATE()?.activeCard)
                );
                return updatedCard;
            }
        } catch (error) {
            console.error(error);
        }
        return this.cards[this.currentIndex];
    }

    private async init(
        scene: Scene,
        canvas: HTMLCanvasElement,
        cardInfo: CardInfo,
        useCardCallback: (cardType: CardType) => unknown
    ): Promise<void> {
        this.clear();

        const updatedCardInfo = await this.updateCardInfo(cardInfo.cardType);
        const { cardType, description, count, comment } = updatedCardInfo;

        this.dynamicElements.nameText = TextUtils.addTextByFamily(scene, description, 0, -170, '#d3b779', 17, 'normal', 420, Fonts.MYRIADPRO_REGULAR);
        const light = SpriteUtils.addSprite(this.scene, 0, -10, Keys.ATLAS_PROFILE, Keys.UI_ART_LIGHT);
        const icon = this.addCardIcon(cardType);
        icon.setScale(0.6);
        this.add([this.dynamicElements.nameText, light, icon]);

        if (count > 0) {
            this.dynamicElements.blockSecond = TextUtils.addTextByFamily(scene, `${Tr('available')}: ${count} ${Tr('pc.')}`, 0, 70, '#918f8f', 14, 'normal', 420, Fonts.ARIAL_MT);
            this.add(this.dynamicElements.blockSecond);
        }

        if (this.isCardActive(cardType)) {
            const textKey = (cardType === CardType.EAGLE)
                ? 'choose.hero.and.improve.rank'
                : 'choose.hero.and.improve.force';
            this.dynamicElements.activeText = TextUtils.addTextByFamily(
                scene,
                `${Tr('card.is.active')}!\n${Tr(textKey)}`,
                0, 120, '#77e877', 18, 'normal', 450, Fonts.ARIAL_MT
            );
            this.add(this.dynamicElements.activeText);
            const cancelText = TextUtils.addTextByFamily(
                scene, Tr("cancel.card.action"), 0, 180, "#ff4444", 16, "normal", 450, Fonts.ARIAL_MT
            );
            cancelText.setOrigin(0.5).setInteractive();
            scene.time.delayedCall(10, () => {
                const textWidth = cancelText.displayWidth;
                const textHeight = cancelText.displayHeight;
                let underline = scene.add.graphics();
                underline.lineStyle(2, 0xff4444, 1);
                underline.moveTo(cancelText.x - textWidth / 2, cancelText.y + textHeight / 2 + 5);
                underline.lineTo(cancelText.x + textWidth / 2, cancelText.y + textHeight / 2 + 5);
                underline.strokePath();
                const cancelContainer = scene.add.container(0, 0, [cancelText, underline]);
                cancelText.on("pointerdown", async () => {
                    try {
                        await CardStore.cancelActiveCard();
                        SoundUtils.play(this.completedSound);
                        this.close();
                        cancelContainer.destroy();
                    } catch (error) {
                        console.error(error);
                    }
                });

                this.add(cancelContainer);
            });
        } else if (cardType === CardType.MOUSE && !this.isAnyCreatureHaveForceUpgrade()) {
            this.dynamicElements.activeText = TextUtils.addTextByFamily(
                scene,
                Tr('no.force.upgrades'),
                0, 120, '#77e877', 18, 'normal', 450, Fonts.ARIAL_MT
            );
            this.add(this.dynamicElements.activeText);
        } else {
            this.dynamicElements.btn = this.createCardButton(scene, canvas, cardType, useCardCallback, count);
        }

        if (this.dynamicElements.evolutionLink) {
            this.dynamicElements.evolutionLink.destroy();
            this.dynamicElements.evolutionLink = undefined;
        }

        if (cardType === CardType.EVOLUTION_UNIVERSAL) {
            this.dynamicElements.evolutionLink = new EvolutionLink(scene, canvas, 0, 225, Tr('detail'), '#D0D0D0');
            this.add(this.dynamicElements.evolutionLink);
        }

        if (comment) {
            this.dynamicElements.commentText = TextUtils.addTextByFamily(scene, comment, 0, 250, '#888888', 14, 'normal', 420, Fonts.ARIAL_MT);
            this.add(this.dynamicElements.commentText);
        }

        if (this.cards.length > 1 && !this.isCardActive(cardType)) {
            this.dynamicElements.leftArrow = this.createArrow(scene, -170, 0, -1, useCardCallback);
            this.dynamicElements.rightArrow = this.createArrow(scene, 170, 0, 1, useCardCallback);
            this.add([this.dynamicElements.leftArrow, this.dynamicElements.rightArrow]);
        }
    }


    private createCardButton(
        scene: Scene,
        canvas: HTMLCanvasElement,
        cardType: CardType,
        useCardCallback: (cardType: CardType) => unknown,
        count: number
    ): SquareButton {
        const button = this.addCustomButton(0, 140, Tr('ispolzovat').toUpperCase(), Keys.UI_ART_GREEN_BUTTON, Keys.ATLAS_PROFILE, Keys.UI_ART_UPPER_ICON, async () => {
            if (count > 0 && !this.isAnyCardActive(cardType)) {
                const ip = new SoloIntroPanel(scene, canvas, CardPanel.getCardName(cardType).toUpperCase(), Tr('you.activated.card'), undefined, CardPanel.getCardSmallImage(cardType), this.getCardTutorial(cardType), "", () => {
                    ip.close();
                });
                ip.openAnimated();
                await useCardCallback(cardType);
                this.cards = await CardStore.fetchCardsInfo();
                if (!this.cards.some(card => card.count > 0) && !PlayerStore.GET_CARDS_STATE()?.activeCard) {
                    this.close();
                    return;
                }
                this.refreshPanel(scene, canvas, useCardCallback);
            }
        }) as SquareButton;
        button.setScale(0.8);
        return button;
    }

    private createArrow(scene: Scene, x: number, y: number, direction: number, useCardCallback: (cardType: CardType) => unknown): Sprite {
        const arrow = SpriteUtils.addSprite(scene, x, y, Keys.ATLAS_PROFILE, direction === -1 ? Keys.UI_ARROW_LEFT : Keys.UI_ARROW_RIGHT);
        arrow.setInteractive().on('pointerdown', () => {
            this.changeCard(direction, useCardCallback);
        });
        return arrow;
    }

    private changeCard(direction: number, useCardCallback: (cardType: CardType) => unknown): void {
        const newIndex = (this.currentIndex + direction + this.cards.length) % this.cards.length;
        if (newIndex !== this.currentIndex) {
            this.currentIndex = newIndex;
            const cardInfo = this.cards[this.currentIndex];
            this.init(this.scene, this.canvas, cardInfo, useCardCallback);
        }
    }

    private isCardActive(cardType: CardType): boolean {
        const activeCard = PlayerStore.GET_CARDS_STATE()?.activeCard;
        return activeCard === cardType;
    }

    private isAnyCardActive(cardType: CardType): boolean {
        const activeCard = PlayerStore.GET_CARDS_STATE()?.activeCard;
        if (!activeCard) {
            return false;
        }
        return activeCard !== cardType;
    }


    public static getCardName(cardType: CardType): string {
        const cardNames = {
            [CardType.ELEPHANT]: Tr('card.elephant'),
            [CardType.SNAKE]: Tr('card.snake'),
            [CardType.EAGLE]: Tr('card.eagle'),
            [CardType.MOUSE]: Tr('card.mouse'),
            [CardType.EVOLUTION_UNIVERSAL]: Tr('card.evolution.universal'),
            [CardType.EVOLUTION_ACE]: Tr('card.evolution.ace'),
            [CardType.EVOLUTION_LUCK]: Tr('card.evolution.luck'),
            [CardType.EVOLUTION_CHAOS]: Tr('card.evolution.chaos'),
            [CardType.EVOLUTION_DEATH]: Tr('card.evolution.death'),
            [CardType.EVOLUTION_DRYAD]: Tr('card.evolution.dryad'),
            [CardType.EVOLUTION_FACELESS]: Tr('card.evolution.faceless'),
            [CardType.EVOLUTION_GORGON]: Tr('card.evolution.gorgon'),
            [CardType.EVOLUTION_HUMAN]: Tr('card.evolution.human'),
            [CardType.EVOLUTION_INKUB]: Tr('card.evolution.inkub'),
            [CardType.EVOLUTION_KOSCHEY]: Tr('card.evolution.koschey'),
            [CardType.EVOLUTION_MANTICORE]: Tr('card.evolution.manticore'),
            [CardType.EVOLUTION_NUN]: Tr('card.evolution.nun'),
            [CardType.EVOLUTION_PHOENIX]: Tr('card.evolution.phoenix'),
            [CardType.EVOLUTION_RIDER]: Tr('card.evolution.rider'),
            [CardType.EVOLUTION_SHADOW]: Tr('card.evolution.shadow'),
            [CardType.EVOLUTION_SPHINX]: Tr('card.evolution.sphinx'),
            [CardType.EVOLUTION_SUKKUB]: Tr('card.evolution.sukkub'),
            [CardType.EVOLUTION_WITCH]: Tr('card.evolution.witch'),
            [CardType.EVOLUTION_DRUID]: Tr('card.evolution.druid'),
        };
        return cardNames[cardType] || "";
    }

    private getCardTutorial(cardType: CardType): string {
        const tutorials = {
            [CardType.ELEPHANT]: Tr('card.elephant.tutorial'),
            [CardType.SNAKE]: Tr('card.snake.tutorial'),
            [CardType.EAGLE]: Tr('card.eagle.tutorial'),
            [CardType.MOUSE]: Tr('card.mouse.tutorial'),
            [CardType.EVOLUTION_UNIVERSAL]: Tr('card.evolution.universal.tutorial'),
            [CardType.EVOLUTION_ACE]: Tr('card.evolution.ace.tutorial'),
            [CardType.EVOLUTION_LUCK]: Tr('card.evolution.luck.tutorial'),
            [CardType.EVOLUTION_CHAOS]: Tr('card.evolution.chaos.tutorial'),
            [CardType.EVOLUTION_DEATH]: Tr('card.evolution.death.tutorial'),
            [CardType.EVOLUTION_DRYAD]: Tr('card.evolution.dryad.tutorial'),
            [CardType.EVOLUTION_FACELESS]: Tr('card.evolution.faceless.tutorial'),
            [CardType.EVOLUTION_GORGON]: Tr('card.evolution.gorgon.tutorial'),
            [CardType.EVOLUTION_HUMAN]: Tr('card.evolution.human.tutorial'),
            [CardType.EVOLUTION_INKUB]: Tr('card.evolution.inkub.tutorial'),
            [CardType.EVOLUTION_KOSCHEY]: Tr('card.evolution.koschey.tutorial'),
            [CardType.EVOLUTION_MANTICORE]: Tr('card.evolution.manticore.tutorial'),
            [CardType.EVOLUTION_NUN]: Tr('card.evolution.nun.tutorial'),
            [CardType.EVOLUTION_PHOENIX]: Tr('card.evolution.phoenix.tutorial'),
            [CardType.EVOLUTION_RIDER]: Tr('card.evolution.rider.tutorial'),
            [CardType.EVOLUTION_SHADOW]: Tr('card.evolution.shadow.tutorial'),
            [CardType.EVOLUTION_SPHINX]: Tr('card.evolution.sphinx.tutorial'),
            [CardType.EVOLUTION_SUKKUB]: Tr('card.evolution.sukkub.tutorial'),
            [CardType.EVOLUTION_WITCH]: Tr('card.evolution.witch.tutorial'),
            [CardType.EVOLUTION_DRUID]: Tr('card.evolution.druid.tutorial'),
        };
        return tutorials[cardType] || "";
    }

    public static getCardSmallImage(cardType: CardType): string {
        const images = {
            [CardType.ELEPHANT]: Keys.CARD_ELEPHANT_SMALL,
            [CardType.SNAKE]: Keys.CARD_SNAKE_SMALL,
            [CardType.EAGLE]: Keys.CARD_EAGLE_SMALL,
            [CardType.MOUSE]: Keys.CARD_MOUSE_SMALL,
            [CardType.EVOLUTION_UNIVERSAL]: Keys.CARD_EVOLUTION_UNIVERSAL_SMALL,
            [CardType.EVOLUTION_ACE]: Keys.CARD_EVOLUTION_ACE_SMALL,
            [CardType.EVOLUTION_LUCK]: Keys.CARD_EVOLUTION_LUCK_SMALL,
            [CardType.EVOLUTION_CHAOS]: Keys.CARD_EVOLUTION_CHAOS_SMALL,
            [CardType.EVOLUTION_DEATH]: Keys.CARD_EVOLUTION_DEATH_SMALL,
            [CardType.EVOLUTION_DRYAD]: Keys.CARD_EVOLUTION_DRYAD_SMALL,
            [CardType.EVOLUTION_FACELESS]: Keys.CARD_EVOLUTION_FACELESS_SMALL,
            [CardType.EVOLUTION_GORGON]: Keys.CARD_EVOLUTION_GORGON_SMALL,
            [CardType.EVOLUTION_HUMAN]: Keys.CARD_EVOLUTION_HIGHLANDER_SMALL,
            [CardType.EVOLUTION_INKUB]: Keys.CARD_EVOLUTION_INCUBUS_SMALL,
            [CardType.EVOLUTION_KOSCHEY]: Keys.CARD_EVOLUTION_KOSCHEI_SMALL,
            [CardType.EVOLUTION_MANTICORE]: Keys.CARD_EVOLUTION_MANTICORE_SMALL,
            [CardType.EVOLUTION_NUN]: Keys.CARD_EVOLUTION_NUN_SMALL,
            [CardType.EVOLUTION_PHOENIX]: Keys.CARD_EVOLUTION_PHOENIX_SMALL,
            [CardType.EVOLUTION_RIDER]: Keys.CARD_EVOLUTION_RIDER_SMALL,
            [CardType.EVOLUTION_SHADOW]: Keys.CARD_EVOLUTION_SHADOW_SMALL,
            [CardType.EVOLUTION_SPHINX]: Keys.CARD_EVOLUTION_SPHINX_SMALL,
            [CardType.EVOLUTION_SUKKUB]: Keys.CARD_EVOLUTION_SUCCUBUS_SMALL,
            [CardType.EVOLUTION_WITCH]: Keys.CARD_EVOLUTION_WITCH_SMALL,
            [CardType.EVOLUTION_DRUID]: Keys.CARD_EVOLUTION_DRUID_SMALL,
        };
        return images[cardType] || "";
    }

    private addCardIcon(cardType: CardType): Sprite {
        const icons = {
            [CardType.ELEPHANT]: Keys.CARD_ELEPHANT,
            [CardType.SNAKE]: Keys.CARD_SNAKE,
            [CardType.EAGLE]: Keys.CARD_EAGLE,
            [CardType.MOUSE]: Keys.CARD_MOUSE,
            [CardType.EVOLUTION_UNIVERSAL]: Keys.CARD_EVOLUTION_UNIVERSAL,
            [CardType.EVOLUTION_ACE]: Keys.CARD_EVOLUTION_ACE,
            [CardType.EVOLUTION_LUCK]: Keys.CARD_EVOLUTION_LUCK,
            [CardType.EVOLUTION_CHAOS]: Keys.CARD_EVOLUTION_CHAOS,
            [CardType.EVOLUTION_DEATH]: Keys.CARD_EVOLUTION_DEATH,
            [CardType.EVOLUTION_DRYAD]: Keys.CARD_EVOLUTION_DRYAD,
            [CardType.EVOLUTION_FACELESS]: Keys.CARD_EVOLUTION_FACELESS,
            [CardType.EVOLUTION_GORGON]: Keys.CARD_EVOLUTION_GORGON,
            [CardType.EVOLUTION_HUMAN]: Keys.CARD_EVOLUTION_HIGHLANDER,
            [CardType.EVOLUTION_INKUB]: Keys.CARD_EVOLUTION_INCUBUS,
            [CardType.EVOLUTION_KOSCHEY]: Keys.CARD_EVOLUTION_KOSCHEI,
            [CardType.EVOLUTION_MANTICORE]: Keys.CARD_EVOLUTION_MANTICORE,
            [CardType.EVOLUTION_NUN]: Keys.CARD_EVOLUTION_NUN,
            [CardType.EVOLUTION_PHOENIX]: Keys.CARD_EVOLUTION_PHOENIX,
            [CardType.EVOLUTION_RIDER]: Keys.CARD_EVOLUTION_RIDER,
            [CardType.EVOLUTION_SHADOW]: Keys.CARD_EVOLUTION_SHADOW,
            [CardType.EVOLUTION_SPHINX]: Keys.CARD_EVOLUTION_SPHINX,
            [CardType.EVOLUTION_SUKKUB]: Keys.CARD_EVOLUTION_SUCCUBUS,
            [CardType.EVOLUTION_WITCH]: Keys.CARD_EVOLUTION_WITCH,
            [CardType.EVOLUTION_DRUID]: Keys.CARD_EVOLUTION_DRUID,
        };
        return SpriteUtils.addSprite(this.scene, 0, -20, CardPanel.getCardIcon(cardType));
    }

    public static getCardIcon(cardType: CardType): string {
        const icons = {
            [CardType.ELEPHANT]: Keys.CARD_ELEPHANT,
            [CardType.SNAKE]: Keys.CARD_SNAKE,
            [CardType.EAGLE]: Keys.CARD_EAGLE,
            [CardType.MOUSE]: Keys.CARD_MOUSE,
            [CardType.EVOLUTION_UNIVERSAL]: Keys.CARD_EVOLUTION_UNIVERSAL,
            [CardType.EVOLUTION_ACE]: Keys.CARD_EVOLUTION_ACE,
            [CardType.EVOLUTION_LUCK]: Keys.CARD_EVOLUTION_LUCK,
            [CardType.EVOLUTION_CHAOS]: Keys.CARD_EVOLUTION_CHAOS,
            [CardType.EVOLUTION_DEATH]: Keys.CARD_EVOLUTION_DEATH,
            [CardType.EVOLUTION_DRYAD]: Keys.CARD_EVOLUTION_DRYAD,
            [CardType.EVOLUTION_FACELESS]: Keys.CARD_EVOLUTION_FACELESS,
            [CardType.EVOLUTION_GORGON]: Keys.CARD_EVOLUTION_GORGON,
            [CardType.EVOLUTION_HUMAN]: Keys.CARD_EVOLUTION_HIGHLANDER,
            [CardType.EVOLUTION_INKUB]: Keys.CARD_EVOLUTION_INCUBUS,
            [CardType.EVOLUTION_KOSCHEY]: Keys.CARD_EVOLUTION_KOSCHEI,
            [CardType.EVOLUTION_MANTICORE]: Keys.CARD_EVOLUTION_MANTICORE,
            [CardType.EVOLUTION_NUN]: Keys.CARD_EVOLUTION_NUN,
            [CardType.EVOLUTION_PHOENIX]: Keys.CARD_EVOLUTION_PHOENIX,
            [CardType.EVOLUTION_RIDER]: Keys.CARD_EVOLUTION_RIDER,
            [CardType.EVOLUTION_SHADOW]: Keys.CARD_EVOLUTION_SHADOW,
            [CardType.EVOLUTION_SPHINX]: Keys.CARD_EVOLUTION_SPHINX,
            [CardType.EVOLUTION_SUKKUB]: Keys.CARD_EVOLUTION_SUCCUBUS,
            [CardType.EVOLUTION_WITCH]: Keys.CARD_EVOLUTION_WITCH,
            [CardType.EVOLUTION_DRUID]: Keys.CARD_EVOLUTION_DRUID,
        };
        return icons[cardType] || "";
    }

    private refreshPanel(scene: Scene, canvas: HTMLCanvasElement, useCardCallback: (cardType: CardType) => unknown): void {
        const activeCardType = this.getActiveCardType();
        if (activeCardType) {
            this.currentIndex = this.cards.findIndex(card => card.cardType === activeCardType);
        }
        this.init(scene, canvas, this.cards[this.currentIndex], useCardCallback);
    }

    private getActiveCardType(): CardType | null {
        return PlayerStore.GET_CARDS_STATE()?.activeCard || null;
    }

    private isAnyCreatureHaveForceUpgrade(): Boolean {
        return PlayerStore.getPlayer().creatures.some(c => c.forceUpgrades.length > 0);
    }
}
